import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/viden/articles`;
const preloadedList = (company) => {
  const all = (typeof globalThis !== "undefined" && globalThis.__VOTRA_DB_ARTICLES__) || {};
  return Object.values(all).filter((a) => a.company === company);
};

// Nyeste publicerede artikler fra databasen, vist på vidensforsiden for hver virksomhed.
export const DbArticleList = ({ company, dark = false }) => {
  const [items, setItems] = useState(() => preloadedList(company));
  useEffect(() => { axios.get(API, { params: { company } }).then((r) => setItems(r.data)).catch(() => {}); }, [company]);
  if (!items.length) return null;
  const base = company === "business" ? "/business/viden" : "/viden";
  const accent = company === "business" ? "#C8A14A" : "#1D6FE0";
  return (
    <section className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12" data-testid={`db-article-list-${company}`}>
      <h2 className={`font-display text-2xl font-semibold mb-6 ${dark ? "text-white" : "text-slate-900"}`}>Nye artikler</h2>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, 9).map((a) => (
          <Link key={a.slug} to={`${base}/${a.slug}`} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-shadow" data-testid={`db-article-card-${a.slug}`}>
            {a.hero_image_url && <img src={a.hero_image_url} alt="" loading="lazy" className="h-40 w-full object-cover" />}
            <div className="p-5">
              <h3 className="font-semibold text-slate-900 leading-snug mb-2">{a.title}</h3>
              <p className="text-sm text-slate-500 line-clamp-3 mb-3">{a.meta_description}</p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: accent }}>Læs artiklen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

// Diskret bjælke på forsiden med den nyeste publicerede artikel for virksomheden.
export const LatestArticleBar = ({ company }) => {
  const [a, setA] = useState(() => preloadedList(company)[0] || null);
  useEffect(() => { axios.get(API, { params: { company } }).then((r) => setA(r.data[0] || null)).catch(() => {}); }, [company]);
  if (!a) return null;
  const biz = company === "business";
  const to = `${biz ? "/business/viden" : "/viden"}/${a.slug}`;
  const accent = biz ? "#C8A14A" : "#1D6FE0";
  return (
    <section className={biz ? "bg-[#F8F7F4]" : "bg-slate-50"} data-testid={`latest-article-${company}`}>
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12 md:py-16">
        <Link to={to} className="group grid sm:grid-cols-[260px_1fr] items-center gap-6 md:gap-10 rounded-2xl border border-slate-200 bg-white p-4 md:p-5 hover:shadow-lg hover:border-slate-300 transition-shadow" data-testid={`latest-article-link-${company}`}>
          {a.hero_image_url
            ? <img src={a.hero_image_url} alt="" loading="lazy" className="w-full h-40 sm:h-36 object-cover rounded-xl" />
            : <div className="h-36 rounded-xl" style={{ backgroundColor: biz ? "#0A192F" : "#0B1F3A" }} />}
          <div className="min-w-0 pr-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: accent }}>{biz ? "Nyt fra Votra Business" : "Nyt fra Votra"}</div>
            <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-2">{a.title}</h2>
            <p className="text-sm text-slate-500 line-clamp-1 mb-3">{a.meta_description}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: accent }}>Læs artiklen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </div>
        </Link>
      </div>
    </section>
  );
};
